function defineMediaAccordion() {
  class MediaAccordion extends HTMLElement {
    constructor() {
      super();
      this.swiperInstance = null;
      this.itemElements = [];
      this.containerElement = null;
      this.rootElement = null;
      this.currentActiveItem = null;
      this.isMobileMode = false;
      this.isInitializing = true;
      this.resizeTimeout = null;
      this.restoreTimeout = null;
      this.initTimeout = null;
      this.playTimeout = null;
      this.elementReadyAttempts = 0;
      this.swiperReadyAttempts = 0;
      this.handleResize = this.handleResize.bind(this);
      this.handleItemHover = this.handleItemHover.bind(this);
      this.handleItemClick = this.handleItemClick.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.debouncedResize = this.debouncedResize.bind(this);
    }

    get items() {
      return this.itemElements;
    }

    connectedCallback() {
      this.isInitializing = true;
      this.swiperReadyAttempts = 0;
      this.elementReadyAttempts = 0;
      this.initializeWhenReady();
    }

    initializeWhenReady() {
      if (!this.isConnected) return;
      this.rootElement = this.closest('.media-accordion');
      const trackElement = this.querySelector('.media-accordion__track');
      const expectedItemCount = Number(this.rootElement?.dataset.blockCount);
      const itemCount = this.querySelectorAll('.media-accordion__item').length;
      if (!trackElement || (Number.isFinite(expectedItemCount) && itemCount < expectedItemCount)) {
        if (this.elementReadyAttempts >= 100) return;
        this.elementReadyAttempts += 1;
        this.initTimeout = window.setTimeout(() => this.initializeWhenReady(), 0);
        return;
      }

      this.itemElements = Array.from(this.querySelectorAll('.media-accordion__item'));
      this.containerElement = this.querySelector('.media-accordion__container');
      this.currentActiveItem = this.itemElements[0] || null;
      this.items.forEach((item, index) => item.classList.toggle('active', index === 0));
      this.addEventListener('focusin', this.handleFocusIn);
      this.addEventListener('click', this.handleItemClick);
      this.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      this.handleResize();
      window.addEventListener('resize', this.debouncedResize);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.debouncedResize);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      this.removeEventListener('focusin', this.handleFocusIn);
      this.removeEventListener('click', this.handleItemClick);
      this.removeEventListener('keydown', this.handleKeydown);
      this.removeHoverListeners();
      window.clearTimeout(this.resizeTimeout);
      window.clearTimeout(this.restoreTimeout);
      window.clearTimeout(this.initTimeout);
      window.clearTimeout(this.playTimeout);
      this.destroySwiper();
      this.items.forEach((item) => this.controlVideo(item, 'pause'));
    }

    debouncedResize() {
      window.clearTimeout(this.resizeTimeout);
      this.resizeTimeout = window.setTimeout(this.handleResize, 150);
    }

    initSwiper() {
      if (
        this.swiperInstance ||
        !this.isMobileMode ||
        !this.containerElement ||
        !this.items.length ||
        this.rootElement?.classList.contains('media-accordion--folding-cards') ||
        typeof Swiper !== 'function'
      ) {
        return;
      }

      const configuredGap = Number(this.rootElement?.dataset.mobileGap);
      const mobileGap = Number.isFinite(configuredGap) && configuredGap >= 0 ? configuredGap : 16;
      const singleItem = this.items.length === 1;
      const swiperOptions = {
        spaceBetween: mobileGap,
        slidesPerView: singleItem ? 1 : 1.2,
        allowTouchMove: !singleItem,
        autoHeight: true,
        a11y: { enabled: true },
        on: {
          slideChange: () => {
            this.items.forEach((item) => this.controlVideo(item, 'pause'));
            const activeSlide = this.containerElement.querySelector('.swiper-slide-active');
            if (!activeSlide) return;
            this.setActiveItem(activeSlide, false);
            this.controlVideo(activeSlide, 'play');
          },
        },
      };

      if (!singleItem) {
        swiperOptions.breakpoints = {
          500: {
            slidesPerView: 1.8,
            spaceBetween: mobileGap,
          },
          990: {
            slidesPerView: 1.8,
            spaceBetween: mobileGap,
          },
        };
      }

      this.swiperInstance = new Swiper(this.containerElement, swiperOptions);
      this.swiperReadyAttempts = 0;
      const activeSlide = this.containerElement.querySelector('.swiper-slide-active');
      if (!activeSlide) return;
      this.setActiveItem(activeSlide, false);
      this.playTimeout = window.setTimeout(() => this.controlVideo(activeSlide, 'play'), 100);
    }

    destroySwiper() {
      window.clearTimeout(this.playTimeout);
      if (!this.swiperInstance) return;
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }

    handleResize() {
      const shouldBeMobile = window.innerWidth < 1025;
      const modeChanged = shouldBeMobile !== this.isMobileMode;
      this.isMobileMode = shouldBeMobile;

      if (!modeChanged && !this.isInitializing) return;

      window.clearTimeout(this.initTimeout);
      if (shouldBeMobile) {
        this.removeHoverListeners();
        this.initTimeout = window.setTimeout(() => this.initializeSwiperWhenReady(), 50);
      } else {
        this.destroySwiper();
        this.addHoverListeners();
        if (this.currentActiveItem) this.setActiveItem(this.currentActiveItem, true);
      }

      this.isInitializing = false;
    }

    initializeSwiperWhenReady() {
      if (!this.isMobileMode || this.swiperInstance || this.rootElement?.classList.contains('media-accordion--folding-cards')) return;
      if (typeof Swiper === 'function') {
        this.swiperReadyAttempts = 0;
        this.initSwiper();
        return;
      }

      if (this.swiperReadyAttempts >= 100) return;
      this.swiperReadyAttempts += 1;
      this.initTimeout = window.setTimeout(() => this.initializeSwiperWhenReady(), 50);
    }

    addHoverListeners() {
      this.items.forEach((item) => {
        item.removeEventListener('mouseenter', this.handleItemHover);
        item.addEventListener('mouseenter', this.handleItemHover);
        item.removeEventListener('mouseleave', this.handleItemHover);
        item.addEventListener('mouseleave', this.handleItemHover);
      });
    }

    removeHoverListeners() {
      this.items.forEach((item) => {
        item.removeEventListener('mouseenter', this.handleItemHover);
        item.removeEventListener('mouseleave', this.handleItemHover);
      });
    }

    setActiveItem(item, playVideo) {
      if (!item) return;
      this.items.forEach((currentItem) => {
        const isActive = currentItem === item;
        currentItem.classList.toggle('active', isActive);
        if (currentItem.getAttribute('role') === 'button') {
          currentItem.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        }
        if (!isActive) this.controlVideo(currentItem, 'pause');
      });
      this.currentActiveItem = item;
      if (playVideo) this.controlVideo(item, 'play');
    }

    controlVideo(item, action) {
      const videoElement = item?.querySelector('yg-video');
      if (!videoElement) return;
      const method = action === 'play' ? videoElement.playVideo : videoElement.pauseVideo;
      if (typeof method !== 'function') return;
      try {
        const result = method.call(videoElement);
        if (result?.catch) result.catch(() => undefined);
      } catch (error) {
        return error;
      }
    }

    handleItemHover(event) {
      if (event.type === 'mouseenter') {
        this.setActiveItem(event.currentTarget, true);
        if (this.items.length > 3) {
          event.currentTarget.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'nearest',
            inline: 'center',
          });
        }
        return;
      }

      window.clearTimeout(this.restoreTimeout);
      this.restoreTimeout = window.setTimeout(() => {
        const hoverItem = this.items.find((item) => item.matches(':hover'));
        if (!hoverItem && this.currentActiveItem) this.setActiveItem(this.currentActiveItem, true);
      }, 10);
    }

    handleFocusIn(event) {
      const item = event.target.closest('.media-accordion__item');
      if (item && this.items.includes(item)) this.setActiveItem(item, true);
    }

    handleItemClick(event) {
      const item = event.target.closest('.media-accordion__item[role="button"]');
      if (item && this.items.includes(item)) this.setActiveItem(item, true);
    }

    handleKeydown(event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const item = event.target.closest('.media-accordion__item');
      if (!item || !this.items.includes(item) || item.querySelector('a:focus')) return;
      event.preventDefault();
      this.setActiveItem(item, true);
    }

    handleBlockSelect(event) {
      const eventTarget = event.target instanceof Element ? event.target : null;
      const selectedItem = eventTarget?.closest('.media-accordion__item');
      if (!selectedItem || !this.items.includes(selectedItem)) return;
      this.setActiveItem(selectedItem, false);
      if (this.swiperInstance) {
        this.swiperInstance.slideTo(this.items.indexOf(selectedItem));
      } else {
        selectedItem.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }
  }

  if (!window.customElements.get('media-accordion')) {
    window.customElements.define('media-accordion', MediaAccordion);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', defineMediaAccordion, { once: true });
} else {
  defineMediaAccordion();
}
